import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import {
  runE2ESignupTest,
  getRecentE2ERuns,
  type E2ETestResult,
  type E2ETestStep,
  type E2ERunRecord,
} from '../../services/firebase';

/**
 * The steps the callable runs, in order. Duplicated here so the panel can show
 * the full checklist greyed-out *before* the run starts and while it's in
 * flight — the callable is a single round-trip, so there's no progress stream to
 * subscribe to. Names must match `e2eSignupTest.ts` exactly; anything the server
 * reports that isn't in this list is still rendered (see `mergeSteps`), so a
 * drift here degrades the preview rather than hiding results.
 */
const EXPECTED_STEPS: { name: string; label: string }[] = [
  { name: 'sendVerificationCode', label: 'Send verification code + queue email' },
  { name: 'readVerificationCode', label: 'Read code back from Firestore' },
  { name: 'verifyCode', label: 'Verify code and consume it' },
  { name: 'createAuthUser', label: 'Create auth user (triggers blocking fns)' },
  { name: 'writeUserDoc', label: 'Write user doc (key, terms, plan)' },
  { name: 'createTestWatch', label: 'Add a test watch + subcollection doc' },
  { name: 'purgeAccount', label: 'Purge the account' },
  { name: 'assertTombstone', label: 'Assert tombstone + auth record deleted' },
  { name: 'assertDataGone', label: 'Assert watch data deleted' },
  { name: 'assertSignupBlocked', label: 'Assert purged email is BLOCKED' },
  { name: 'removeTombstone', label: 'Remove tombstone' },
  { name: 'assertSignupUnblocked', label: 'Assert email is reusable again' },
];

/** A step row as rendered: the expected checklist merged with actual results. */
interface DisplayStep {
  name: string;
  label: string;
  result?: E2ETestStep;
}

/**
 * Merge the static checklist with whatever the server actually reported,
 * preserving the server's ordering for known steps and appending any step the
 * UI doesn't know about, so a server-side addition never goes unrendered.
 */
function mergeSteps(result: E2ETestResult | null): DisplayStep[] {
  const byName = new Map<string, E2ETestStep>();
  for (const step of result?.steps ?? []) byName.set(step.name, step);

  const rows: DisplayStep[] = EXPECTED_STEPS.map((expected) => ({
    name: expected.name,
    label: expected.label,
    result: byName.get(expected.name),
  }));

  const known = new Set(EXPECTED_STEPS.map((s) => s.name));
  for (const step of result?.steps ?? []) {
    if (!known.has(step.name)) {
      rows.push({ name: step.name, label: step.name, result: step });
    }
  }
  return rows;
}

function formatMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function formatRunTime(at: Date | null): string {
  if (!at) return '—';
  return at.toLocaleString();
}

/** ✓ / ✗ / spinner / pending dot for one step row. */
function StepIcon({
  result,
  running,
}: {
  result: E2ETestStep | undefined;
  running: boolean;
}) {
  if (result?.ok === true) {
    return <span className="text-green-600 font-semibold">✓</span>;
  }
  if (result && !result.ok) {
    return <span className="text-red-600 font-semibold">✗</span>;
  }
  if (running) {
    return (
      <span
        className="inline-block w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"
        aria-label="running"
      />
    );
  }
  return <span className="text-gray-300">•</span>;
}

/**
 * End-to-end account-lifecycle smoke test (watchlock PR #786).
 *
 * One button that creates a throwaway account, drives it all the way through
 * purge and tombstone removal, and asserts each stage — including that a purged
 * email can no longer sign up, which is the regression that reached production
 * on 2026-07-24.
 *
 * The callable is a single round-trip, so per-step results all arrive at once.
 * Rather than fake a live feed, this renders the full checklist up front with a
 * spinner and an elapsed-time counter, then paints ✓/✗ and timings on return.
 *
 * Lives under Users (its own tab) rather than Data Maintenance: what it
 * exercises is the account lifecycle, not the catalogue.
 */
export function E2ESmokeTest() {
  const [running, setRunning] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [result, setResult] = useState<E2ETestResult | null>(null);
  const [history, setHistory] = useState<E2ERunRecord[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await getRecentE2ERuns(20));
      setHistoryError(null);
    } catch (err) {
      console.error('getRecentE2ERuns failed:', err);
      setHistoryError(
        err instanceof Error ? err.message : 'Failed to load run history',
      );
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // Elapsed-time counter while the single long call is in flight.
  useEffect(() => {
    if (!running) return;
    const startedAt = Date.now();
    setElapsedMs(0);
    const id = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    return () => clearInterval(id);
  }, [running]);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await runE2ESignupTest();
      setResult(r);
      if (r.passed) {
        toast.success(`E2E test passed in ${formatMs(r.totalMs)}`);
      } else {
        toast.error(`E2E test FAILED at "${r.failedStep ?? 'unknown step'}"`);
      }
    } catch (err) {
      // A rejection means the call itself faulted (not a super admin, missing
      // WEB_API_KEY unset) — distinct from a test that ran and failed.
      console.error('runE2ESignupTest failed:', err);
      toast.error(
        err instanceof Error
          ? `Could not run the test: ${err.message}`
          : 'Could not run the test — see console',
      );
    } finally {
      setRunning(false);
      void loadHistory();
    }
  };

  const rows = mergeSteps(result);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">
        E2E Signup Smoke Test
      </h2>

      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-4 max-w-4xl">
        <div className="text-sm text-gray-600 space-y-2">
          <p>
            Creates a throwaway{' '}
            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
              james+e2e-…@jwsadler.org
            </code>{' '}
            account and drives it through the full lifecycle — signup, purge,
            tombstone, reactivation — asserting each stage. Takes roughly 15–25
            seconds. Run it before every deploy.
          </p>
          <p>
            The key assertion is that a{' '}
            <strong>purged email is blocked from signing up again</strong> — the
            regression that reached production on 2026-07-24.
          </p>
          <p className="text-xs text-gray-500">
            Each run sends a real{' '}
            <span className="font-medium">[E2E Smoke Test]</span> email to
            james@jwsadler.org and queues the standard purge notice to
            support@watchkeeper.tech. Device-only behaviour (encryption-key
            storage, push tokens, RevenueCat) is <em>not</em> covered — a green
            run says nothing about billing.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button loading={running} disabled={running} onClick={() => void run()}>
            {result ? 'Run again' : 'Run E2E Test'}
          </Button>
          {running && (
            <span className="text-sm text-gray-500 tabular-nums">
              {formatMs(elapsedMs)} elapsed…
            </span>
          )}
        </div>

        {result && (
          <div
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              result.passed
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {result.passed
              ? `PASSED — all ${result.steps.length} steps in ${formatMs(result.totalMs)}`
              : `FAILED at "${result.failedStep ?? 'unknown step'}" after ${formatMs(result.totalMs)}`}
            <div className="font-normal text-xs mt-1 opacity-80">
              {result.syntheticEmail}
            </div>
          </div>
        )}

        {(running || result) && (
          <ol className="space-y-1">
            {rows.map((row, index) => (
              <li
                key={row.name}
                className={`flex items-start gap-2 text-sm py-1 px-2 rounded ${
                  row.result && !row.result.ok ? 'bg-red-50' : ''
                }`}
              >
                <span className="w-4 text-right text-xs text-gray-400 tabular-nums pt-0.5">
                  {index + 1}
                </span>
                <span className="w-4 pt-0.5">
                  <StepIcon result={row.result} running={running} />
                </span>
                <span
                  className={`flex-1 ${
                    row.result ? 'text-gray-800' : 'text-gray-400'
                  }`}
                >
                  {row.label}
                  {row.result?.error && (
                    <span className="block text-xs text-red-700 mt-0.5 font-mono break-words">
                      {row.result.error}
                    </span>
                  )}
                </span>
                <span className="text-xs text-gray-400 tabular-nums pt-0.5">
                  {row.result ? formatMs(row.result.durationMs) : ''}
                </span>
              </li>
            ))}
          </ol>
        )}

        <div className="pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Recent runs
            </h4>
            <button
              type="button"
              onClick={() => void loadHistory()}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Refresh
            </button>
          </div>

          {historyError && (
            <p className="text-xs text-red-600">{historyError}</p>
          )}

          {!historyError && history.length === 0 && (
            <p className="text-xs text-gray-400">No runs recorded yet.</p>
          )}

          {history.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-1 pr-3 font-medium">Result</th>
                    <th className="py-1 pr-3 font-medium">When</th>
                    <th className="py-1 pr-3 font-medium">Duration</th>
                    <th className="py-1 pr-3 font-medium">Triggered by</th>
                    <th className="py-1 pr-3 font-medium">Failed step</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((run) => (
                    <tr key={run.id} className="border-b border-gray-100">
                      <td className="py-1 pr-3">
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded font-medium ${
                            run.passed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {run.passed ? 'PASS' : 'FAIL'}
                        </span>
                      </td>
                      <td className="py-1 pr-3 text-gray-600 whitespace-nowrap">
                        {formatRunTime(run.at)}
                      </td>
                      <td className="py-1 pr-3 text-gray-600 tabular-nums">
                        {formatMs(run.totalMs)}
                      </td>
                      <td className="py-1 pr-3 text-gray-600">
                        {run.triggeredByEmail ?? run.triggeredBy}
                      </td>
                      <td className="py-1 pr-3 text-red-700 font-mono">
                        {run.failedStep ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
